import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import * as locales from "date-fns/locale"

import {
  DateFnsTzUtils,
  MuiPickersTzUtilsProvider,
} from "providers/DateFns/MuiPickerUtilsTzProvider"
import {
  Tokens,
  TokensPayload,
  getRefreshTokens,
  getTokenFromCode,
  getTokens,
} from "api/auth"

import type { Authority } from "./authorities"
import Emittery from "emittery"
import { Loading } from "components/Loading"
import { date_display_format_obj } from "utils/dates"
import decodeToken from "jwt-decode"
import { http } from "api/http"
import { queryCache } from "react-query"
import { useHistory } from "react-router-dom"
import { useLocationQuery } from "hooks/useLocationQuery"
import { useAgencyId } from "hooks/useAgencyId"

export type { Authority } from "./authorities"

export const TEAM_LEAD_AUTHORITY = "AUTH_TEAM_LEADER_ADMIN_ACCESS"

const emitter = new Emittery()

const isTokenExpired = (token?: string): boolean => {
  if (!token) return true
  try {
    const tk: any = decodeToken(token)
    const expiration = +new Date(tk.exp * 1e3)
    return Date.now() > expiration
  } catch (_e) {
    return true
  }
}

const getValidToken = (key: string) => {
  const token = localStorage.getItem(key) || undefined
  return isTokenExpired(token) ? undefined : token
}

const clearTokens = () => {
  localStorage.removeItem("accessToken")
  localStorage.removeItem("refreshToken")
}

const saveTokens = ({ accessToken, refreshToken }: Tokens) => {
  localStorage.setItem("accessToken", accessToken)
  refreshToken && localStorage.setItem("refreshToken", refreshToken)
}

export const logout = async () => {
  clearTokens()
  await emitter.emit("logout")
}

export const login = async (payload: TokensPayload) => {
  const tokens = await getTokens(payload)
  saveTokens(tokens)
  return tokens
}

export const winLogin = async (code: string) => {
  const tokens = await getTokenFromCode(code)
  saveTokens(tokens)
  return tokens
}

export const getToken = async () => {
  try {
    const accessToken = getValidToken("accessToken")
    const refreshToken = getValidToken("refreshToken")
    if (accessToken) {
      return Promise.resolve(accessToken)
    } else if (refreshToken) {
      const res = await getRefreshTokens(refreshToken)
      saveTokens(res)
      return res.accessToken
    } else {
      return null
    }
  } catch (e) {
    return null
  }
}

export type Role =
  | "GLOBAL_ADMINISTRATOR"
  | "MARKET_ADMINISTRATOR"
  | "AGENCY_ADMINISTRATOR"
  | "AGENCY_FINANCE"
  | "MANAGING_DIRECTOR"
  | "TEAM_LEAD"
  | "PLANNER"
  | "CLIENT"
  | "MARKET_FINANCE"
  | "BUYER"
  | "BILLER"
  | "GLOBAL_FINANCE"
  | "REGIONAL_FINANCE"

type User = {
  name: string
  preferred_username: string
  family_name: string
  given_name: string
  sub: string
  scope: string
  agency_id?: string
}

export type Meta = {
  id: string
  layerId?: string
  company: string
  email: string
  location: string
  surname: string
  userRole: Role
  userAuthorities: Authority[]
  timeZone: string
  //
  teamLead: boolean
  decimalPrecision?: number
  firstName: string
  lastName: string
}

type AuthenticatedContextType = {
  user: User | null
  meta?: Meta
  logout: () => void
  hasAuthority: (allowed: Authority[]) => boolean
  dateFormatter: (
    d: string | number | Date | null | undefined,
    options?: Intl.DateTimeFormatOptions | undefined,
  ) => string
  updateUserMeta: () => Promise<void>
}

type AuthenticatedState = {
  decoded: User | null
  meta?: Meta
  changedAt: number
}

export const AuthenticatedContext = createContext<
  AuthenticatedContextType | undefined
>(undefined)

export function useAuthenticated(): AuthenticatedContextType {
  const ctx = useContext(AuthenticatedContext)
  if (ctx === undefined) {
    throw new Error("useAuthenticated must be used inside Authenticated!")
  }
  return ctx
}

const getInitialUser = async (at: string | null) => {
  try {
    const token = at || (await getToken())
    const decoded = token ? decodeToken<User>(token) : null
    return {
      decoded: decoded,
      changedAt: Date.now(),
    }
  } catch (_e) {
    return {
      decoded: null,
      changedAt: Date.now(),
    }
  }
}

const getUserMeta = async (accessToken: string | null) => {
  const headers = accessToken
    ? { Authorization: "Bearer " + accessToken }
    : undefined

  try {
    const data = await http
      .get("users/user-data", {
        headers,
      })
      .json()

    return {
      meta: data as Meta,
      changedAt: Date.now(),
    }
  } catch (error) {
    // do nothing
  }
}

const applyTeamLeadAuthority = (userMeta?: Meta) => {
  if (userMeta?.teamLead && userMeta?.userAuthorities) {
    userMeta.userAuthorities.push(TEAM_LEAD_AUTHORITY)
  }
}

export function checkAuthority(
  meta: Meta | undefined,
  authorities: Authority[],
): boolean {
  return !!meta?.userAuthorities?.some((ua) => authorities.includes(ua))
}

export function localizedDateFormatter(locale: Locale, timeZone: string) {
  return function (
    d: string | number | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = date_display_format_obj,
  ) {
    if (d === undefined || d === null) {
      return "-"
    }
    return new Date(d).toLocaleString(locale.code, {
      timeZone,
      ...options,
    })
  }
}

export function Authenticated(props: { children: ReactNode }) {
  const { children } = props
  const history = useHistory()
  const localeStr = navigator?.language?.replace("-", "")
  const locale = locales[localeStr as keyof typeof locales]
  const [state, setState] = useState<AuthenticatedState>({
    changedAt: 0,
    decoded: null,
  })
  const { changedAt, decoded, meta } = state
  const qs = useLocationQuery()

  const handleInitial = async () => {
    const accessToken = qs.get("access_token")
    if (accessToken) {
      saveTokens({ accessToken })
    }
    const iu = await getInitialUser(accessToken)
    const um = await getUserMeta(accessToken)

    applyTeamLeadAuthority(um?.meta)

    setState({ ...iu, ...um })
  }

  const updateUserMeta = async () => {
    const accessToken = qs.get("access_token")
    const upMeta = await getUserMeta(accessToken)
    setState((s) => ({ ...s, ...upMeta }))
  }

  useEffect(() => {
    if (changedAt === 0) {
      handleInitial()
    }
    if (changedAt > 0 && decoded === null) {
      history.replace("/login")
    }
    // eslint-disable-next-line
  }, [changedAt])

  const { clearAgencyID } = useAgencyId()

  const handleLogout = useCallback(() => {
    setState({ decoded: null, changedAt: Date.now() })
    queryCache.clear()
    // Reset agencyId for Agency Admin & Finance users
    clearAgencyID()
  }, [clearAgencyID])

  const dateFormatter = localizedDateFormatter(
    locale || locales["enGB"],
    meta?.timeZone || "UTC",
  )

  useEffect(() => {
    emitter.on("logout", handleLogout)
    return () => {
      emitter.off("logout", handleLogout)
    }
  }, [handleLogout])

  const ctx = useMemo(() => {
    const hasAuthority = (allowed: Authority[]) => checkAuthority(meta, allowed)

    if (0) {
      console.log("A12n:user authorities::", meta?.userAuthorities ?? "nada")
    }

    return {
      user: decoded,
      meta,
      logout,
      hasAuthority,
      dateFormatter,
      updateUserMeta,
    }
    // eslint-disable-next-line
  }, [changedAt])

  return changedAt > 0 ? (
    <AuthenticatedContext.Provider value={ctx}>
      <MuiPickersTzUtilsProvider
        utils={DateFnsTzUtils}
        locale={locale || locales["enGB"]}
        timeZone={meta?.timeZone || "UTC"}
      >
        {children}
      </MuiPickersTzUtilsProvider>
    </AuthenticatedContext.Provider>
  ) : (
    <Loading />
  )
}

export const DEFAULT_PRECISION = 2

export function usePrecision() {
  const { meta } = useAuthenticated()
  return meta?.decimalPrecision || DEFAULT_PRECISION
}
